import Component from '@glimmer/component';
import { service } from '@ember/service';
import Heading from 'tio-reimagined/components/heading';
import Drawer from 'tio-reimagined/components/drawer';
import { formatCurrency } from 'tio-reimagined/format';
import { t } from 'ember-intl';
import {
  PhCalendarDots,
  PhSealCheck,
  PhMoneyWavy,
  PhHourglassLow,
  PhCalendarBlank,
} from 'ember-phosphor-icons';
import type PlanModel from 'tio-common/models/plan';
import type IntlService from 'ember-intl/services/intl';
import type SessionContextService from 'tio-employee/services/session-context';

/**
 * BenefitDetailDrawer — View Benefit plan details in a right drawer
 *
 * Displays plan name, contribution start date, waiting period
 * (if applicable), contribution amount with cadence, annual limit
 * (if applicable), and lifetime limit (if applicable).
 *
 * @example
 * ```gts
 * <BenefitDetailDrawer
 *   @isOpen={{this.showBenefitDrawer}}
 *   @onClose={{this.closeBenefitDrawer}}
 *   @plan={{this.plan}}
 * />
 * ```
 */
interface BenefitDetailDrawerSignature {
  Args: {
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Called when the drawer should close */
    onClose: () => void;
    /** The employee's plan model */
    plan: PlanModel | null | undefined;
  };
  Element: HTMLDivElement;
}

export default class BenefitDetailDrawer extends Component<BenefitDetailDrawerSignature> {
  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;

  get plan() {
    return this.args.plan;
  }

  get planName() {
    return this.plan?.name || this.intl.t('accounts.benefit_detail.default_plan_name');
  }

  get planDescription() {
    return this.plan?.description;
  }

  get hasWaitingPeriod() {
    const months = this.plan?.waitingPeriod;
    return months != null && months > 0;
  }

  get hasAnnualLimit() {
    const limit = this.plan?.annualLimit;
    return limit != null && limit > 0;
  }

  get hasLifetimeLimit() {
    const limit = this.plan?.lifetimeLimit;
    return limit != null && limit > 0;
  }

  get annualLimit() {
    const limit = this.plan?.annualLimit;
    if (limit == null) return '—';
    return formatCurrency(limit / 100);
  }

  get lifetimeLimit() {
    const limit = this.plan?.lifetimeLimit;
    if (limit == null) return '—';
    return formatCurrency(limit / 100);
  }

  // --- Contribution start date ---

  /**
   * Resolves the contribution start date as a Date object,
   * applying the waiting period offset for _PLUS_WAIT milestones.
   */
  get contributionStartDateValue() {
    const employee = this.sessionContext.currentEmployee;
    const milestone = this.plan?.milestoneDate?.toUpperCase();
    let dateStr: string | undefined;

    if (milestone === 'ELIGIBILITY_DATE' || milestone === 'ELIGIBILITY_DATE_PLUS_WAIT') {
      dateStr = employee?.eligibilityStartDate;
    } else if (milestone === 'START_DATE' || milestone === 'START_DATE_PLUS_WAIT') {
      dateStr = employee?.employmentStartDate;
    } else {
      // FIRST_CONTRIBUTION or TODAY — fall back to eligibility or employment
      dateStr = employee?.eligibilityStartDate || employee?.employmentStartDate;
    }

    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;

    // For _PLUS_WAIT milestones, add waiting period to the base date
    if (milestone?.endsWith('_PLUS_WAIT') && this.plan?.waitingPeriod) {
      const unit = this.plan.waitingPeriodUnit?.toLowerCase();
      if (unit === 'days') {
        d.setDate(d.getDate() + this.plan.waitingPeriod);
      } else {
        // Default to months (matches tio-payment behavior)
        d.setMonth(d.getMonth() + this.plan.waitingPeriod);
      }
    }

    return d;
  }

  get contributionStartDate() {
    const d = this.contributionStartDateValue;
    if (!d) return '';

    return new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }).format(d);
  }

  get cadenceUnit() {
    switch (this.plan?.cadence?.toUpperCase()) {
      case 'MONTHLY':
        return this.intl.t('accounts.benefit_detail.per_month');
      case 'QUARTERLY':
        return this.intl.t('accounts.benefit_detail.per_quarter');
      case 'SEMI_ANNUALLY':
        return this.intl.t('accounts.benefit_detail.per_semi_annual');
      case 'ANNUALLY':
        return this.intl.t('accounts.benefit_detail.per_year');
      default:
        return '';
    }
  }

  /**
   * Number of contribution periods per year for the plan's cadence.
   */
  get periodsPerYear() {
    switch (this.plan?.cadence?.toUpperCase()) {
      case 'MONTHLY':
        return 12;
      case 'QUARTERLY':
        return 4;
      case 'SEMI_ANNUALLY':
        return 2;
      case 'ANNUALLY':
        return 1;
      default:
        return 12;
    }
  }

  /**
   * Whether the tier duration is already captured by annual or lifetime limits.
   * If true, don't show duration on the contribution amount row.
   * If false, show estimated end year instead.
   */
  get isTierDurationCapturedByLimits() {
    const tiers = this.plan?.tiers;
    if (!tiers?.length) return true;

    const hasDuration = tiers.some((tier) => tier.per > 0);
    if (!hasDuration) return true;

    // If tier total matches lifetime limit, the duration is captured
    const tierTotal = tiers.reduce((sum, tier) => sum + tier.amount * tier.per, 0);
    if (this.plan?.lifetimeLimit && tierTotal === this.plan.lifetimeLimit) {
      return true;
    }

    // If per-year amount matches annual limit, the duration is captured
    const firstTier = tiers[0];
    if (firstTier && this.plan?.annualLimit) {
      const annualFromTier = firstTier.amount * this.periodsPerYear;
      if (annualFromTier === this.plan.annualLimit) {
        return true;
      }
    }

    return false;
  }

  /**
   * Estimated end year from total tier periods,
   * anchored to the contribution start date (not today).
   */
  get estimatedEndYear() {
    const tiers = this.plan?.tiers;
    if (!tiers?.length) return null;

    const totalPeriods = tiers.reduce((sum, tier) => sum + tier.per, 0);
    if (totalPeriods <= 0) return null;

    const cadence = this.plan?.cadence?.toUpperCase();
    let monthsPerPeriod = 1;
    if (cadence === 'QUARTERLY') monthsPerPeriod = 3;
    if (cadence === 'SEMI_ANNUALLY') monthsPerPeriod = 6;
    if (cadence === 'ANNUALLY') monthsPerPeriod = 12;

    const totalMonths = totalPeriods * monthsPerPeriod;
    const startDate = this.contributionStartDateValue || new Date();
    const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + totalMonths, 1);
    return endDate.getFullYear().toString();
  }

  /**
   * Formatted contribution amount including cadence, e.g. "$50/month".
   * Per spec:
   * - If tier duration IS captured by limits → just "$50/month"
   * - If tier duration is NOT captured by limits → "$50/month until ~2028"
   */
  get contributionWithCadence() {
    const tiers = this.plan?.tiers;
    if (!tiers?.length) return '—';
    const amount = tiers[0]?.amount;
    if (!amount) return '—';
    const formattedAmount = formatCurrency(amount / 100);
    const unit = this.cadenceUnit;
    if (!unit) return formattedAmount;

    let label = `${formattedAmount}/${unit}`;

    // If tier duration is NOT captured by limits, show estimated end year
    if (!this.isTierDurationCapturedByLimits && this.estimatedEndYear) {
      label += ` ${this.intl.t('accounts.benefit_detail.until_year', { year: this.estimatedEndYear })}`;
    }

    return label;
  }

  get waitingPeriodLabel() {
    const months = this.plan?.waitingPeriod;
    if (months == null || months === 0) return '—';
    if (months === 1) return this.intl.t('accounts.benefit_detail.employed_one_month');
    return this.intl.t('accounts.benefit_detail.employed_months', { months });
  }

  <template>
    <Drawer @isOpen={{@isOpen}} @onClose={{@onClose}} @size="lg" @placement="right" ...attributes>
      <:header>
        <div class="flex flex-col gap-2">
          <Heading @level={{3}} class="text-xl font-semibold text-gray-900">
            {{this.planName}}
          </Heading>
          {{#if this.planDescription}}
            <p class="font-medium leading-relaxed text-gray-500 text-base">
              {{this.planDescription}}
            </p>
          {{/if}}
        </div>
      </:header>
      <:body>
        <div class="flex flex-col gap-8 px-8 py-10">
          {{! Contribution Start Date }}
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <PhCalendarBlank @size={{20}} @weight="fill" class="text-gray-600" />
              <span class="font-semibold text-gray-700 text-base">{{t
                  "accounts.benefit_detail.contribution_start_date"
                }}</span>
            </div>
            <span class="font-medium text-gray-500 text-base">{{if
                this.contributionStartDate
                this.contributionStartDate
                "—"
              }}</span>
          </div>

          {{! Contribution Amount with cadence }}
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <PhMoneyWavy @size={{20}} @weight="fill" class="text-gray-600" />
              <span class="font-semibold text-gray-700 text-base">{{t
                  "accounts.benefit_detail.contribution_amount"
                }}</span>
            </div>
            <span
              class="font-medium text-gray-500 text-base"
            >{{this.contributionWithCadence}}</span>
          </div>

          {{! Waiting Period }}
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <PhHourglassLow @size={{20}} @weight="fill" class="text-gray-600" />
              <span class="font-semibold text-gray-700 text-base">{{t
                  "accounts.benefit_detail.waiting_period"
                }}</span>
            </div>
            <span class="font-medium text-gray-500 text-base">{{this.waitingPeriodLabel}}</span>
          </div>

          {{! Annual Limit }}
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <PhCalendarDots @size={{20}} @weight="fill" class="text-gray-600" />
              <span class="font-semibold text-gray-700 text-base">{{t
                  "accounts.benefit_detail.annual_limit"
                }}</span>
            </div>
            <span class="font-medium text-gray-500 text-base">{{this.annualLimit}}</span>
          </div>

          {{! Lifetime Limit }}
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-1.5">
              <PhSealCheck @size={{20}} @weight="fill" class="text-gray-600" />
              <span class="font-semibold text-gray-700 text-base">{{t
                  "accounts.benefit_detail.lifetime_limit"
                }}</span>
            </div>
            <span class="font-medium text-gray-500 text-base">{{this.lifetimeLimit}}</span>
          </div>

        </div>
      </:body>
    </Drawer>
  </template>
}
// test translation check
// test
// test-translation-check
